import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { siteConfig } from '../config/siteConfig';
import RotatingTestimonials from '../components/RotatingTestimonials';

function HomePage() {
  const { t } = useTranslation();
  const [zipCode, setZipCode] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (zipCode.trim() && zipCode.length === 5) {
      navigate(`/smart-matching-wizard?zip=${zipCode}`);
    }
  };

  const sectionStyle = {
    padding: 'clamp(2rem, 5vw, 5rem) 0'
  };

  const cardStyle = {
    backgroundColor: 'white',
    padding: 'clamp(1.5rem, 4vw, 2rem)',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    textAlign: 'center',
    border: '1px solid #e5e7eb',
    height: '100%'
  };

  return (
    <>
      <Helmet>
        <title>{t('home.seo.title')}</title>
        <meta name="description" content={t('home.seo.description')} />
        <meta name="keywords" content={t('home.seo.keywords')} />
      </Helmet>
      {/* SIMPLIFIED HERO SECTION - UX-3.1 */}
      {/* Mobile-first, single clear headline, 2 CTAs max, navy/orange only */}
      <section style={{
        background: `linear-gradient(135deg, #1b3a57 0%, #0c2c47 100%)`,
        color: 'white',
        padding: 'clamp(2rem, 6vw, 4rem) 0'
      }}>
        <div className="ffp-container-reading" style={{ textAlign: 'center' }}>

          {/* Single Clear Headline */}
          <h1 style={{
            fontSize: 'clamp(1.75rem, 5vw, 2.75rem)',
            fontWeight: 'bold',
            marginBottom: '1rem',
            fontFamily: 'Montserrat, sans-serif',
            color: 'white',
            lineHeight: '1.2',
            maxWidth: '700px',
            margin: '0 auto 1rem'
          }}>
            {t('home.hero.title')}
          </h1>

          {/* Simple Subtitle */}
          <p style={{
            fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
            marginBottom: '2rem',
            color: 'rgba(255, 255, 255, 0.85)',
            fontFamily: 'Open Sans, sans-serif',
            lineHeight: '1.5',
            maxWidth: '600px',
            margin: '0 auto 2rem'
          }}>
            {t('home.hero.subtitle')}
          </p>

          {/* ZIP Search Form - Primary CTA */}
          <form onSubmit={handleSearch} style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            maxWidth: '400px',
            margin: '0 auto 1.5rem'
          }}>
            <input
              type="text"
              value={zipCode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                if (value.length <= 5) setZipCode(value);
              }}
              placeholder="Enter your ZIP code"
              style={{
                padding: '1rem 1.25rem',
                fontSize: '1.125rem',
                border: 'none',
                outline: 'none',
                color: '#1f2937',
                backgroundColor: 'white',
                fontFamily: 'Open Sans, sans-serif',
                textAlign: 'center'
              }}
              required
              maxLength={5}
              pattern="[0-9]{5}"
            />
            <button
              type="submit"
              style={{
                padding: '1rem 2rem',
                backgroundColor: '#ea5416',
                color: 'white',
                border: 'none',
                fontSize: '1.125rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontFamily: 'Montserrat, sans-serif',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#d44a12'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#ea5416'}
            >
              Get Help Now
            </button>
          </form>

          {/* Secondary CTA - Find Contractors (no ZIP required) */}
          <button
            onClick={() => navigate('/find-contractors')}
            style={{
              padding: '0.875rem 2rem',
              backgroundColor: 'transparent',
              color: 'white',
              border: '2px solid rgba(255, 255, 255, 0.5)',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              fontFamily: 'Montserrat, sans-serif',
              transition: 'all 0.2s',
              marginBottom: '2rem'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.8)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
            }}
          >
            Find Contractors
          </button>

          {/* Trust Indicators - Compact */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 'clamp(1rem, 4vw, 2.5rem)',
            flexWrap: 'wrap',
            fontSize: 'clamp(0.8rem, 2vw, 0.9rem)',
            color: 'rgba(255, 255, 255, 0.75)'
          }}>
            <span>24/7 Emergency Response</span>
            <span>Free Service Matching</span>
            <span>Verified Professionals</span>
          </div>

        </div>
      </section>

      {/* Trust Indicators - Service Features */}
      <section style={{ padding: '1.5rem 0', backgroundColor: 'white' }}>
        <div className="ffp-container-reading">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div style={{ padding: '1rem' }}>
              <div style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', marginBottom: '0.5rem' }}>🏠</div>
              <div style={{ fontSize: 'clamp(0.75rem, 2vw, 0.95rem)', color: '#374151', fontFamily: 'Open Sans, sans-serif', fontWeight: '600' }}>
{t('home.trust.residential')}
              </div>
            </div>
            <div style={{ padding: '1rem' }}>
              <div style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', marginBottom: '0.5rem' }}>📋</div>
              <div style={{ fontSize: 'clamp(0.75rem, 2vw, 0.95rem)', color: '#374151', fontFamily: 'Open Sans, sans-serif', fontWeight: '600' }}>
{t('home.trust.insurance')}
              </div>
            </div>
            <div style={{ padding: '1rem' }}>
              <div style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', marginBottom: '0.5rem' }}>🚨</div>
              <div style={{ fontSize: 'clamp(0.75rem, 2vw, 0.95rem)', color: '#374151', fontFamily: 'Open Sans, sans-serif', fontWeight: '600' }}>
{t('home.trust.emergency.title')}
              </div>
            </div>
            <div style={{ padding: '1rem' }}>
              <div style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', marginBottom: '0.5rem' }}>🛡️</div>
              <div style={{ fontSize: 'clamp(0.75rem, 2vw, 0.95rem)', color: '#374151', fontFamily: 'Open Sans, sans-serif', fontWeight: '600' }}>
{t('home.trust.verified')}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Live Stats Counter - Platform Credibility */}
      <section style={{ padding: '2rem 0', backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' }}>
        <div className="ffp-container-reading">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 'bold', color: '#ea5416', fontFamily: 'Montserrat, sans-serif' }}>
                50+
              </div>
              <div style={{ fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)', color: '#374151', fontFamily: 'Open Sans, sans-serif' }}>
                Verified Contractors
              </div>
            </div>
            <div>
              <div style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 'bold', color: '#0c2c47', fontFamily: 'Montserrat, sans-serif' }}>
                2,500+
              </div>
              <div style={{ fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)', color: '#374151', fontFamily: 'Open Sans, sans-serif' }}>
                Projects Completed
              </div>
            </div>
            <div>
              <div style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 'bold', color: '#ea5416', fontFamily: 'Montserrat, sans-serif' }}>
                4.8★
              </div>
              <div style={{ fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)', color: '#374151', fontFamily: 'Open Sans, sans-serif' }}>
                Average Rating
              </div>
            </div>
            <div>
              <div style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 'bold', color: '#0c2c47', fontFamily: 'Montserrat, sans-serif' }}>
                {"<"}1 hr
              </div>
              <div style={{ fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)', color: '#374151', fontFamily: 'Open Sans, sans-serif' }}>
                Avg Response Time
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Services Section */}
      <section style={{ ...sectionStyle, backgroundColor: 'white' }}>
        <div className="ffp-container-reading">
          <h2 style={{
            fontSize: 'clamp(1.5rem, 4vw, 2.25rem)',
            fontWeight: 'bold',
            textAlign: 'center',
            marginBottom: 'clamp(2rem, 5vw, 4rem)',
            color: '#0c2c47',
            fontFamily: 'Montserrat, sans-serif'
          }}>
{t('home.emergencyServices.title')}
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 'clamp(1.5rem, 3vw, 2rem)'
          }}>
            <div
              style={{...cardStyle, cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s'}}
              onClick={() => navigate('/water-damage-restoration')}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
              }}
            >
              <div style={{ fontSize: 'clamp(2.5rem, 5vw, 3.5rem)', marginBottom: '1rem', color: '#2563eb' }}>💧</div>
              <h3 style={{ fontSize: 'clamp(1.25rem, 3vw, 1.5rem)', fontWeight: 'bold', marginBottom: '1rem', color: '#0c2c47', fontFamily: 'Montserrat, sans-serif' }}>
{t('home.emergencyServices.water.title')}
              </h3>
              <p style={{ color: '#6b7280', lineHeight: '1.6', fontFamily: 'Open Sans, sans-serif', fontSize: 'clamp(0.875rem, 2vw, 1rem)', marginBottom: '1rem' }}>
{t('home.emergencyServices.water.description')}
              </p>
              <div style={{ color: '#2563eb', fontWeight: 'bold', fontSize: '0.9rem' }}>
{t('home.emergencyServices.water.cta')}
              </div>
            </div>

            <div
              style={{...cardStyle, cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s'}}
              onClick={() => navigate('/fire-damage-restoration')}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
              }}
            >
              <div style={{ fontSize: 'clamp(2.5rem, 5vw, 3.5rem)', marginBottom: '1rem', color: '#dc2626' }}>🔥</div>
              <h3 style={{ fontSize: 'clamp(1.25rem, 3vw, 1.5rem)', fontWeight: 'bold', marginBottom: '1rem', color: '#0c2c47', fontFamily: 'Montserrat, sans-serif' }}>
{t('home.emergencyServices.fire.title')}
              </h3>
              <p style={{ color: '#6b7280', lineHeight: '1.6', fontFamily: 'Open Sans, sans-serif', fontSize: 'clamp(0.875rem, 2vw, 1rem)', marginBottom: '1rem' }}>
{t('home.emergencyServices.fire.description')}
              </p>
              <div style={{ color: '#dc2626', fontWeight: 'bold', fontSize: '0.9rem' }}>
{t('home.emergencyServices.fire.cta')}
              </div>
            </div>

            <div
              style={{...cardStyle, cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s'}}
              onClick={() => navigate('/mold-remediation')}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
              }}
            >
              <div style={{ fontSize: 'clamp(2.5rem, 5vw, 3.5rem)', marginBottom: '1rem', color: '#059669' }}>🦠</div>
              <h3 style={{ fontSize: 'clamp(1.25rem, 3vw, 1.5rem)', fontWeight: 'bold', marginBottom: '1rem', color: '#0c2c47', fontFamily: 'Montserrat, sans-serif' }}>
{t('home.emergencyServices.mold.title')}
              </h3>
              <p style={{ color: '#6b7280', lineHeight: '1.6', fontFamily: 'Open Sans, sans-serif', fontSize: 'clamp(0.875rem, 2vw, 1rem)', marginBottom: '1rem' }}>
{t('home.emergencyServices.mold.description')}
              </p>
              <div style={{ color: '#059669', fontWeight: 'bold', fontSize: '0.9rem' }}>
{t('home.emergencyServices.mold.cta')}
              </div>
            </div>

            <div
              style={{...cardStyle, cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s'}}
              onClick={() => navigate('/get-help-now')}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
              }}
            >
              <div style={{ fontSize: 'clamp(2.5rem, 5vw, 3.5rem)', marginBottom: '1rem', color: '#ea5416' }}>🚨</div>
              <h3 style={{ fontSize: 'clamp(1.25rem, 3vw, 1.5rem)', fontWeight: 'bold', marginBottom: '1rem', color: '#0c2c47', fontFamily: 'Montserrat, sans-serif' }}>
{t('home.emergencyServices.emergency.title')}
              </h3>
              <p style={{ color: '#6b7280', lineHeight: '1.6', fontFamily: 'Open Sans, sans-serif', fontSize: 'clamp(0.875rem, 2vw, 1rem)', marginBottom: '1rem' }}>
{t('home.emergencyServices.emergency.description')}
              </p>
              <div style={{ color: '#ea5416', fontWeight: 'bold', fontSize: '0.9rem' }}>
{t('home.emergencyServices.emergency.cta')}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section style={{ ...sectionStyle, backgroundColor: '#f8f9fa' }}>
        <div className="ffp-container-reading">
          <h2 style={{
            fontSize: 'clamp(1.5rem, 4vw, 2.25rem)',
            fontWeight: 'bold',
            textAlign: 'center',
            marginBottom: 'clamp(2rem, 5vw, 4rem)',
            color: '#0c2c47',
            fontFamily: 'Montserrat, sans-serif'
          }}>
{t('home.whyChoose.title')}
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 'clamp(1.5rem, 3vw, 2rem)'
          }}>
            <div style={cardStyle}>
              <div style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', marginBottom: '1rem', color: '#ea5416' }}>🛡️</div>
              <h3 style={{ fontSize: 'clamp(1.125rem, 3vw, 1.25rem)', fontWeight: 'bold', marginBottom: '0.75rem', color: '#0c2c47', fontFamily: 'Montserrat, sans-serif' }}>
{t('home.whyChoose.certified.title')}
              </h3>
              <p style={{ color: '#6b7280', lineHeight: '1.6', fontFamily: 'Open Sans, sans-serif', fontSize: 'clamp(0.875rem, 2vw, 1rem)' }}>
{t('home.whyChoose.certified.description')}
              </p>
            </div>

            <div style={cardStyle}>
              <div style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', marginBottom: '1rem', color: '#ea5416' }}>💰</div>
              <h3 style={{ fontSize: 'clamp(1.125rem, 3vw, 1.25rem)', fontWeight: 'bold', marginBottom: '0.75rem', color: '#0c2c47', fontFamily: 'Montserrat, sans-serif' }}>
{t('home.whyChoose.free.title')}
              </h3>
              <p style={{ color: '#6b7280', lineHeight: '1.6', fontFamily: 'Open Sans, sans-serif', fontSize: 'clamp(0.875rem, 2vw, 1rem)' }}>
{t('home.whyChoose.free.description')}
              </p>
            </div>

            <div style={cardStyle}>
              <div style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', marginBottom: '1rem', color: '#1b5a91' }}>⚡</div>
              <h3 style={{ fontSize: 'clamp(1.125rem, 3vw, 1.25rem)', fontWeight: 'bold', marginBottom: '0.75rem', color: '#0c2c47', fontFamily: 'Montserrat, sans-serif' }}>
{t('home.whyChoose.instant.title')}
              </h3>
              <p style={{ color: '#6b7280', lineHeight: '1.6', fontFamily: 'Open Sans, sans-serif', fontSize: 'clamp(0.875rem, 2vw, 1rem)' }}>
{t('home.whyChoose.instant.description')}
              </p>
            </div>

            <div style={cardStyle}>
              <div style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', marginBottom: '1rem', color: '#1b5a91' }}>📍</div>
              <h3 style={{ fontSize: 'clamp(1.125rem, 3vw, 1.25rem)', fontWeight: 'bold', marginBottom: '0.75rem', color: '#0c2c47', fontFamily: 'Montserrat, sans-serif' }}>
{t('home.whyChoose.local.title')}
              </h3>
              <p style={{ color: '#6b7280', lineHeight: '1.6', fontFamily: 'Open Sans, sans-serif', fontSize: 'clamp(0.875rem, 2vw, 1rem)' }}>
{t('home.whyChoose.local.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges Section - APOLLO Visual Enhancement: Real SVG badges replace emoji placeholders */}
      <section style={{ padding: '2.5rem 0', backgroundColor: 'white', borderTop: '1px solid #e5e7eb' }}>
        <div className="ffp-container-reading">
          <p style={{
            textAlign: 'center',
            fontSize: 'clamp(0.8rem, 1.5vw, 0.875rem)',
            color: '#6b7280',
            marginBottom: '1.5rem',
            fontFamily: 'Open Sans, sans-serif',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            Our Contractors Are Certified By
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
            {/* IICRC Badge */}
            <div className="flex flex-col items-center">
              <img
                src="/images/badges/iicrc-certified.svg"
                alt="IICRC Certified - Institute of Inspection Cleaning and Restoration Certification"
                style={{ width: '80px', height: '60px', marginBottom: '0.5rem' }}
              />
              <span style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '600' }}>IICRC Certified</span>
            </div>
            {/* Licensed & Insured */}
            <div className="flex flex-col items-center">
              <img
                src="/images/badges/licensed-insured.svg"
                alt="Licensed and Insured Contractors"
                style={{ width: '80px', height: '60px', marginBottom: '0.5rem' }}
              />
              <span style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '600' }}>Licensed & Insured</span>
            </div>
            {/* Background Checked */}
            <div className="flex flex-col items-center">
              <img
                src="/images/badges/background-checked.svg"
                alt="Background Verified Professionals"
                style={{ width: '80px', height: '60px', marginBottom: '0.5rem' }}
              />
              <span style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '600' }}>Background Checked</span>
            </div>
            {/* EPA Certified */}
            <div className="flex flex-col items-center">
              <img
                src="/images/badges/epa-lead-safe.svg"
                alt="EPA Lead-Safe Certified"
                style={{ width: '80px', height: '60px', marginBottom: '0.5rem' }}
              />
              <span style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '600' }}>EPA Lead-Safe</span>
            </div>
            {/* 24/7 Service */}
            <div className="flex flex-col items-center">
              <img
                src="/images/badges/emergency-24-7.svg"
                alt="24/7 Emergency Response Available"
                style={{ width: '80px', height: '60px', marginBottom: '0.5rem' }}
              />
              <span style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '600' }}>24/7 Emergency</span>
            </div>
          </div>
        </div>
      </section>

      {/* Rotating Testimonials Section */}
      <RotatingTestimonials />

      {/* Final CTA Section */}
  <section style={{ background: `linear-gradient(135deg, ${siteConfig.colors.secondary} 0%, ${siteConfig.colors.secondaryDark} 100%)`, ...sectionStyle, textAlign: 'center', color: 'white' }}>
        <div className="ffp-container-reading">
          <h2 style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)', fontWeight: 'bold', marginBottom: '1rem' }}>
{t('home.finalCta.title')}
          </h2>
          <p style={{ fontSize: 'clamp(1rem, 2.5vw, 1.125rem)', marginBottom: '2rem', color: '#e2e8f0' }}>
{t('home.finalCta.description')}
          </p>

          <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <button
              onClick={() => navigate('/find-contractors/')}
              style={{
                padding: 'clamp(0.75rem, 2vw, 1rem) clamp(1.5rem, 4vw, 2rem)',
                backgroundColor: siteConfig.colors.primary,
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: 'clamp(0.875rem, 2.5vw, 1rem)',
                fontWeight: 'bold',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
{t('home.finalCta.button')}
            </button>
          </div>
        </div>
      </section>

    </>
  );
}

export default HomePage;
